import React, { useState } from 'react';
import { useFormikContext } from 'formik';
import { IoMdWarning } from 'react-icons/io';
import dayjs from 'dayjs';

import InfoTooltip from './InfoTooltip';
import { useGetEventRecurringSeries } from '../../../events/hooks';
import Spinner, { SpinnerType } from '../spinner/Spinner';
import {
  RECURRING_FREQUENCY_LABELS,
  RecurringFrequency
} from '../../../events/types';
import Modal, { ModalType } from '../modal/Modal';
import Button, { ButtonType } from '../button/Button';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

interface Props {
  eventId: string;
  navigateToRecurringSettings: () => void;
}

const computeFrequency = (
  frequency: RecurringFrequency,
  startDate: string,
  numberOfEvents: number,
  t: TFunction
) => {
  const frequencyText = RECURRING_FREQUENCY_LABELS[frequency];
  const startDay = dayjs(startDate).format('dddd');
  return t('events:host.repeatingEventFrequency', {
    frequency: frequencyText,
    startDay,
    numberOfEvents
  });
};

const RecurringEvent = ({ navigateToRecurringSettings, eventId }: Props) => {
  const { t } = useTranslation(['events', 'common']);
  const [isRequirementsModalOpen, setIsRequirementsModalOpen] = useState(false);
  const { values } = useFormikContext<any>();
  const { data, isLoading } = useGetEventRecurringSeries(
    { eventId },
    { enabled: !!eventId && !!values.seriesId }
  );

  const toggleRequirementsModal = () => {
    setIsRequirementsModalOpen((prevState) => !prevState);
  };

  const handleRecurringEventClick = () => {
    if (
      !eventId ||
      !values.title ||
      !values.photo?.url ||
      !values.description ||
      !values.address ||
      !values.coordinates
    ) {
      return toggleRequirementsModal();
    }

    return navigateToRecurringSettings();
  };

  return (
    <>
      <div className="w-full mr-10 pt-7 text-base text-gray-500 flex items-center justify-between">
        <div className="flex items-center">
          <img src="/img/icons/sync.svg" className="mr-2 w-[20px]" />
          {t('events:host.repeat')}
          <InfoTooltip
            id="recurring-event-tooltip"
            message={t('events:host.repeatingEventTooltip')}
          />
        </div>
        {isLoading ? (
          <Spinner type={SpinnerType.PRIMARY} />
        ) : (
          <button
            type="button"
            className="py-1 pl-2 text-primary-500 text-base font-semibold"
            onClick={handleRecurringEventClick}
          >
            {data
              ? computeFrequency(
                  data.frequency,
                  data.startDate,
                  data.eventIds.length,
                  t
                )
              : t('events:host.doesNotRepeat')}
          </button>
        )}
      </div>
      <Modal
        visible={isRequirementsModalOpen}
        onClose={toggleRequirementsModal}
        type={ModalType.SMALL}
        autoHeight
        title={t('events:host.requiredFieldsMissing')}
        icon={
          <div className="bg-yellow-100 flex-1 h-full flex items-center justify-center text-yellow-800">
            <IoMdWarning size={20} />
          </div>
        }
        renderFooter={() => (
          <Button
            type={ButtonType.Primary}
            onClick={toggleRequirementsModal}
            newRadius
          >
            {t('common:buttons.ok')}
          </Button>
        )}
      >
        <div className="p-4">
          <p className="text-base font-normal text-neutral-600">
            {t('events:host.repeatingEventTooltipDescription')}
          </p>
        </div>
      </Modal>
    </>
  );
};

export default RecurringEvent;
